import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, MessageCircle, Navigation } from 'lucide-react';
import { Button, Card, Input, Textarea, Reveal } from '../components/common';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Handle form submission here
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleWhatsAppClick = () => {
    const encodedMessage = encodeURIComponent(
      `New Inquiry from AngelCite Website\n\n` +
      `Name: ${formData.name || 'Not provided'}\n` +
      `Email: ${formData.email || 'Not provided'}\n` +
      `Subject: ${formData.subject || 'General Inquiry'}\n` +
      `Message: ${formData.message || 'I would like to know more about your products'}`
    );
    window.open(`https://wa.me/+23057432920?text=${encodedMessage}`, '_blank');
  };

  const handleQuickWhatsApp = (topic: string) => {
    const message = encodeURIComponent(
      `Hello! I'm interested in ${topic} from AngelCite. Could you please provide more information?`
    );
    window.open(`https://wa.me/+23057432920?text=${message}`, '_blank');
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: '+263 77 123 4567',
      link: 'tel:+263771234567',
    },
    {
      icon: Mail,
      title: 'Email',
      details: 'info@angelcite.com',
      link: 'mailto:info@angelcite.com',
    },
    {
      icon: MapPin,
      title: 'Address',
      details: 'The Avenues, Harare, Zimbabwe',
      link: 'https://maps.google.com/?q=The+Avenues+Harare+Zimbabwe',
    },
    {
      icon: Clock,
      title: 'Hours',
      details: 'Mon-Sun: 8am - 8pm',
      link: null,
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      details: '+230 5743 2920',
      link: 'https://wa.me/+23057432920',
    },
  ];

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent-light to-primary opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Have questions about our products? Need support? We're here to help!
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid lg:grid-cols-3 gap-8 md:gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <Reveal animation="slide-right">
              <h2 className="text-2xl font-bold text-white dark:text-dark-dark mb-6 transition-colors duration-300">
                Contact Information
              </h2>
            </Reveal>
            
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <Reveal key={info.title} animation="slide-up" delay={index * 100}>
                  <Card className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                        <info.icon className="w-6 h-6 text-accent dark:text-primary transition-colors duration-300" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white dark:text-dark-dark mb-1 transition-colors duration-300">
                          {info.title}
                        </h3>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-gray-300 dark:text-gray-600 hover:text-accent dark:hover:text-primary transition-colors duration-300"
                            target={info.link.startsWith('http') ? '_blank' : undefined}
                            rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          >
                            {info.details}
                          </a>
                        ) : (
                          <p className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                            {info.details}
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                </Reveal>
              ))}
            </div>

            {/* Quick WhatsApp Buttons */}
            <div className="mt-8">
              <Reveal animation="slide-up">
                <h3 className="text-xl font-semibold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
                  Quick Contact via WhatsApp
                </h3>
              </Reveal>
              <div className="space-y-3">
                {[
                  { topic: 'Product Inquiry', color: 'bg-green-500 hover:bg-green-600' },
                  { topic: 'Order Status', color: 'bg-blue-500 hover:bg-blue-600' },
                  { topic: 'Delivery Questions', color: 'bg-purple-500 hover:bg-purple-600' },
                  { topic: 'Wholesale Prices', color: 'bg-orange-500 hover:bg-orange-600' },
                ].map((item, index) => (
                  <Reveal key={item.topic} animation="slide-up" delay={index * 50}>
                    <button
                      onClick={() => handleQuickWhatsApp(item.topic)}
                      className={`w-full p-3 ${item.color} text-white rounded-lg flex items-center justify-between transition-colors duration-300 group`}
                    >
                      <span className="font-medium">{item.topic}</span>
                      <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                    </button>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-8">
              <Reveal animation="slide-up">
                <h3 className="text-xl font-semibold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
                  Frequently Asked
                </h3>
              </Reveal>
              <div className="space-y-3">
                {[
                  'How do I track my order?',
                  'What is your return policy?',
                  'Do you deliver to my area?',
                  'Are all products organic?',
                ].map((question, index) => (
                  <Reveal key={question} animation="slide-up" delay={index * 50}>
                    <a
                      href="/faq"
                      className="block p-3 bg-dark-light dark:bg-gray-50 rounded-lg hover:bg-dark dark:hover:bg-gray-100 transition-colors duration-300 group"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300 dark:text-gray-600 group-hover:text-accent dark:group-hover:text-primary transition-colors duration-300">
                          {question}
                        </span>
                        <span className="text-gray-400 group-hover:text-accent dark:group-hover:text-primary transition-colors duration-300">
                          →
                        </span>
                      </div>
                    </a>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form & Map */}
          <div className="lg:col-span-2">
            <Reveal animation="slide-left">
              <Card className="p-6 md:p-8">
                <h2 className="text-2xl font-bold text-white dark:text-dark-dark mb-6 transition-colors duration-300">
                  Send us a Message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                    />
                    <Input
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  
                  <Input
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    required
                  />
                  
                  <Textarea
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your inquiry..."
                    rows={6}
                    required
                  />
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      className="sm:flex-1"
                      onClick={handleWhatsAppClick}
                      leftIcon={<Send className="w-5 h-5" />}
                    >
                      Send Message
                    </Button>
                  </div>
                </form>
              </Card>
            </Reveal>

            {/* Map Section - Harare, The Avenues */}
            <div className="mt-8">
              <Reveal animation="slide-up" delay={200}>
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white dark:text-dark-dark transition-colors duration-300">
                        Our Location in Harare
                      </h3>
                      <p className="text-gray-300 dark:text-gray-600 mt-1 transition-colors duration-300">
                        The Avenues, Harare - Visit us or get directions
                      </p>
                    </div>
                    <a
                      href="https://maps.google.com/maps?q=The+Avenues,+Harare,+Zimbabwe"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-accent dark:bg-primary text-white rounded-lg hover:opacity-90 transition-opacity duration-200"
                    >
                      <Navigation className="w-4 h-4" />
                      Get Directions
                    </a>
                  </div>
                  
                  <div className="aspect-[16/9] rounded-lg overflow-hidden border border-dark-light dark:border-gray-300">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3799.755056308786!2d31.044192315441793!3d-17.825959887859097!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1931a5d5d5d5d5d5%3A0x5d5d5d5d5d5d5d5d!2sThe%20Avenues%2C%20Harare!5e0!3m2!1sen!2szw!4v1645567890123!5m2!1sen!2szw"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="AngelCite Location - The Avenues, Harare"
                      className="bg-dark-light dark:bg-gray-100"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <Reveal animation="scale" delay={0}>
                      <div className="text-center p-4 bg-dark-light dark:bg-gray-50 rounded-lg">
                        <MapPin className="w-6 h-6 text-accent dark:text-primary mx-auto mb-2 transition-colors duration-300" />
                        <p className="text-sm text-gray-300 dark:text-gray-600 transition-colors duration-300">
                          Central Location
                        </p>
                      </div>
                    </Reveal>
                    <Reveal animation="scale" delay={100}>
                      <div className="text-center p-4 bg-dark-light dark:bg-gray-50 rounded-lg">
                        <Clock className="w-6 h-6 text-accent dark:text-primary mx-auto mb-2 transition-colors duration-300" />
                        <p className="text-sm text-gray-300 dark:text-gray-600 transition-colors duration-300">
                          Easy Access
                        </p>
                      </div>
                    </Reveal>
                    <Reveal animation="scale" delay={200}>
                      <div className="text-center p-4 bg-dark-light dark:bg-gray-50 rounded-lg">
                        <MessageCircle className="w-6 h-6 text-accent dark:text-primary mx-auto mb-2 transition-colors duration-300" />
                        <p className="text-sm text-gray-300 dark:text-gray-600 transition-colors duration-300">
                          WhatsApp Directions
                        </p>
                      </div>
                    </Reveal>
                  </div>
                </Card>
              </Reveal>
            </div>

            {/* WhatsApp Direct Contact */}
            <div className="mt-6">
              <Reveal animation="fade-in" delay={300}>
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="text-center md:text-left">
                      <h3 className="text-xl font-bold text-white mb-2">
                        Need Immediate Assistance?
                      </h3>
                      <p className="text-green-100">
                        Chat with us directly on WhatsApp for faster response
                      </p>
                    </div>
                    <a
                      href="https://wa.me/+23057432920"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-6 py-3 bg-white text-green-600 rounded-lg font-semibold hover:bg-green-50 transition-colors duration-200"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Chat Now on WhatsApp
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
